import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FaUser,
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaClock,
    FaTag,
    FaCommentDots,
} from 'react-icons/fa';
import { RiArrowRightUpLine } from 'react-icons/ri';
import './ContactForm.css';
import { usePublicSettings } from '../../context/PublicSettingsContext';
import useContact from '../../hooks/useContact';
import LanguageContext from '../../context/LanguageContext.js';
import { hasPublicAddress } from '../../utils/formatPublicAddress.js';
import { formatShiftRange, weekdayLabel } from '../../utils/openHoursLabel.js';

const ContactForm = () => {
    const { dir } = useContext(LanguageContext);
    const { t } = useTranslation();
    const { publicSettings } = usePublicSettings();
    const { loading, success, submitContactForm, resetStates } = useContact();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
    });
    const [focused, setFocused] = useState(null);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            await submitContactForm(formData);

            setTimeout(() => {
                resetStates();
                setFormData({
                    name: '',
                    email: '',
                    subject: '',
                    message: '',
                });
            }, 4000);
        } catch (err) {
            console.error('Contact form submission error:', err);
        }
    };

    const address = publicSettings?.address;
    const showAddress = hasPublicAddress(address);

    const infoItems = [
        {
            icon: <FaMapMarkerAlt />,
            label: t('contact.address'),
            value: showAddress ? (
                <>
                    {address.street && <>{address.street}<br /></>}
                    {(address.city || address.state || address.zipCode) && (
                        <>
                            {address.city}
                            {address.state && `, ${address.state}`}
                            {address.zipCode && ` ${address.zipCode}`}
                            <br />
                        </>
                    )}
                    {address.country}
                </>
            ) : (
                t('contact.addressNotConfigured')
            ),
        },
        {
            icon: <FaPhone />,
            label: t('contact.callUs'),
            value: publicSettings?.phoneNumber ? (
                <a href={`tel:${publicSettings.phoneNumber.replace(/\s/g, '')}`}>{publicSettings.phoneNumber}</a>
            ) : (
                t('contact.phoneNotConfigured')
            ),
        },
        {
            icon: <FaEnvelope />,
            label: t('contact.writeUs'),
            value: publicSettings?.email ? (
                <a href={`mailto:${publicSettings.email}`}>{publicSettings.email}</a>
            ) : (
                t('contact.emailNotConfigured')
            ),
        },
    ];


    const layoutDir = dir();

    return (
        <section className="cf-root">
            <div className="container cf-header" dir={layoutDir}>
                <span className="cf-eyebrow">{t('nav.contact')}</span>
                <h2 className="cf-page-title">{t('contact.getInTouch')}</h2>
                <p className="cf-page-desc">{t('contact.description')}</p>
            </div>

            <div className="container cf-layout" dir={layoutDir}>
                <aside className="cf-info">
                    <div className="cf-info-inner">
                        <p className="cf-info-tagline">{t('contact.sideTagline')}</p>

                        <ul className="cf-contact-list">
                            {infoItems.map((item, i) => (
                                <li key={i} className="cf-contact-item">
                                    <div className="cf-contact-ico">{item.icon}</div>
                                    <div>
                                        <span className="cf-contact-label">{item.label}</span>
                                        <p className="cf-contact-val">{item.value}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <div className="cf-hours-block">
                            <div className="cf-hours-header">
                                <FaClock className="cf-hours-ico" />
                                <span>{t('contact.openHours')}</span>
                            </div>
                            <ul className="cf-hours-list">
                                {publicSettings?.openHours ? (
                                    publicSettings.openHours.map((d) => {
                                        const shifts = d.shifts
                                            .map((s) => formatShiftRange(s.openTime, s.closeTime))
                                            .join(', ');
                                        return (
                                            <li key={d.day} className={`cf-hours-row ${d.isOpen ? '' : 'cf-hours-row--closed'}`}>
                                                <span className="cf-hours-day">{weekdayLabel(d.day, t)}</span>
                                                <span className="cf-hours-time" dir={d.isOpen ? 'ltr' : undefined}>{d.isOpen ? shifts : t('contact.closed')}</span>
                                            </li>
                                        );
                                    })
                                ) : (
                                    <li className="cf-hours-row">
                                        <span>{t('contact.hours')}</span>
                                    </li>
                                )}
                            </ul>
                        </div>

                        {(publicSettings?.specialNotice || publicSettings?.isTemporarilyClosed) && (
                            <div className={`cf-notice ${publicSettings?.isTemporarilyClosed ? 'cf-notice--closed' : ''}`}>
                                <div className="cf-notice-dot" aria-hidden="true" />
                                <div>
                                    <p className="cf-notice-title">
                                        {publicSettings?.isTemporarilyClosed
                                            ? t('contact.temporarilyClosed')
                                            : t('contact.importantNotice')}
                                    </p>
                                    <p className="cf-notice-msg">
                                        {publicSettings?.isTemporarilyClosed
                                            ? publicSettings.temporaryClosureMessage || t('contact.temporarilyClosedMessage')
                                            : publicSettings.specialNotice}
                                    </p>
                                </div>
                            </div>
                        )}

                        <blockquote className="cf-quote" aria-hidden="true">
                            {t('contact.decorQuote')}
                        </blockquote>
                    </div>
                </aside>

                <div className="cf-form-panel">
                    {success ? (
                        <div className="cf-success">
                            <div className="cf-success-ring" aria-hidden="true">
                                <svg viewBox="0 0 60 60" fill="none">
                                    <circle
                                        cx="30"
                                        cy="30"
                                        r="28"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeDasharray="176"
                                        strokeDashoffset="0"
                                        className="cf-success-circle"
                                    />
                                    <path
                                        d="M18 30l9 9 15-15"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="cf-success-check"
                                    />
                                </svg>
                            </div>
                            <h3 className="cf-success-title">{t('contact.successTitle')}</h3>
                            <p className="cf-success-msg">{t('contact.successMessage')}</p>
                        </div>
                    ) : (
                        <form className="cf-form" onSubmit={handleSubmit}>
                            <div className="cf-form-head">
                                <h3 className="cf-form-title">{t('contact.formTitle')}</h3>
                                <p className="cf-form-sub">{t('contact.formSubtitle')}</p>
                            </div>

                            <div className="cf-row">
                                <div
                                    className={`cf-group ${focused === 'name' ? 'cf-group--focus' : ''} ${
                                        formData.name ? 'cf-group--filled' : ''
                                    }`}
                                >
                                    <label className="cf-label" htmlFor="cf-name">
                                        <FaUser className="cf-lbl-ico" />
                                        {t('contact.name')}
                                    </label>
                                    <input
                                        id="cf-name"
                                        type="text"
                                        name="name"
                                        placeholder={t('contact.namePlaceholder')}
                                        className="cf-input"
                                        value={formData.name}
                                        onChange={handleChange}
                                        onFocus={() => setFocused('name')}
                                        onBlur={() => setFocused(null)}
                                        required
                                    />
                                </div>

                                <div
                                    className={`cf-group ${focused === 'email' ? 'cf-group--focus' : ''} ${
                                        formData.email ? 'cf-group--filled' : ''
                                    }`}
                                >
                                    <label className="cf-label" htmlFor="cf-email">
                                        <FaEnvelope className="cf-lbl-ico" />
                                        {t('contact.email')}
                                    </label>
                                    <input
                                        id="cf-email"
                                        type="email"
                                        name="email"
                                        placeholder={t('contact.emailPlaceholder')}
                                        className="cf-input"
                                        value={formData.email}
                                        onChange={handleChange}
                                        onFocus={() => setFocused('email')}
                                        onBlur={() => setFocused(null)}
                                        required
                                    />
                                </div>
                            </div>

                            <div
                                className={`cf-group cf-group--full ${focused === 'subject' ? 'cf-group--focus' : ''} ${
                                    formData.subject ? 'cf-group--filled' : ''
                                }`}
                            >
                                <label className="cf-label" htmlFor="cf-subject">
                                    <FaTag className="cf-lbl-ico" />
                                    {t('contact.subject')}
                                </label>
                                <input
                                    id="cf-subject"
                                    type="text"
                                    name="subject"
                                    placeholder={t('contact.subjectPlaceholder')}
                                    className="cf-input"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    onFocus={() => setFocused('subject')}
                                    onBlur={() => setFocused(null)}
                                    required
                                />
                            </div>

                            <div
                                className={`cf-group cf-group--full ${focused === 'message' ? 'cf-group--focus' : ''} ${
                                    formData.message ? 'cf-group--filled' : ''
                                }`}
                            >
                                <label className="cf-label" htmlFor="cf-message">
                                    <FaCommentDots className="cf-lbl-ico" />
                                    {t('contact.message')}
                                </label>
                                <textarea
                                    id="cf-message"
                                    name="message"
                                    placeholder={t('contact.messagePlaceholder')}
                                    className="cf-input cf-textarea"
                                    value={formData.message}
                                    onChange={handleChange}
                                    onFocus={() => setFocused('message')}
                                    onBlur={() => setFocused(null)}
                                    rows={5}
                                    required
                                />
                            </div>

                            <div className="cf-actions cf-actions--end">
                                <button type="submit" className={`cf-submit ${loading ? 'cf-submit--loading' : ''}`} disabled={loading}>
                                    <span>{loading ? t('contact.sending') : t('contact.sendMessage')}</span>
                                    <span className="cf-submit-ico" aria-hidden="true">
                                        <RiArrowRightUpLine />
                                    </span>
                                    <span className="cf-submit-shine" aria-hidden="true" />
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ContactForm;
